import { getProductStaticCopy } from '@/i18n/product-static-copy';

type WalkthroughTranslator = ((key: string) => string) & {
  has?: (key: string) => boolean;
};

function resolveWithFallback(
  key: string,
  fallback: string,
  t: WalkthroughTranslator
) {
  if (t.has) {
    try {
      if (!t.has(key)) {
        return fallback;
      }
    } catch {
      return fallback;
    }
  }

  try {
    const translated = t(key);
    if (
      translated === key ||
      translated.endsWith(`.${key}`) ||
      translated.startsWith('WalkthroughSteps.')
    ) {
      return fallback;
    }
    return translated;
  } catch {
    return fallback;
  }
}

const AVATAR_WALKTHROUGH_TEXT_KEYS: Record<string, string> = {
  'Avatar Creation Paths': 'avatarsCreationPathsTitle',
  'Open the avatar creation page here. From there you can choose Build your avatar, Create an AI Twin, or Quick Generate.':
    'avatarsCreateDescription',
  Drafts: 'avatarsDraftsTitle',
  'When you have unfinished avatar work, drafts from the avatar builder, AI Twin, and quick-generate flows appear at the top of this page so you can reopen them from their saved preview history.':
    'avatarsDraftsDescription',
  'When you have unfinished avatar work, drafts from the avatar builder, AI Twin, and quick-generate flows appear at the top of this page so you can jump back into them.':
    'avatarsDraftsMobileDescription',
  'Avatar Library': 'avatarsLibraryTitle',
  'Your Avatar Library': 'avatarsLibraryTitle',
  'This library brings together saved drafts, private workspace avatars, and reusable published sets from verified workspaces so you can resume, manage, and reuse characters in one place.':
    'avatarsLibraryDescription',
  'This library brings together drafts from the new avatar flows, private workspace avatars, and reusable published sets from verified workspaces so you can resume, manage, and reuse characters in one place.':
    'avatarsLibraryDescription',
  'Your avatar library lives here. This surface can show private workspace avatars, reusable published sets from verified workspaces, or an empty state while you are getting started.':
    'avatarsLibraryDescription',
  'Browse drafts, workspace avatars, and reusable published sets available to your team.':
    'avatarsLibraryMobileDescription',
  'Browse the avatars created in your workspace and any reusable published sets available to your team.':
    'avatarsLibraryMobileDescription',
  'Created Avatars': 'avatarsCreatedTitle',
  'This section shows the avatars already created in your workspace so you can reopen, manage, and reuse them.':
    'avatarsCreatedDescription',
  'Below your created avatars, this page can also show reusable published sets from verified workspaces, or an empty state while you are getting started.':
    'avatarsLibraryMobileDescription',
  'Use this button to open the avatar creation page, where you can choose Build your avatar, Create an AI Twin, or Quick Generate.':
    'avatarsCreateMobileDescription',
  'Start A New Avatar': 'avatarsCreateTitle',
  'Create An Avatar': 'avatarsCreateTitle',
  'Open the avatar starter here. You can jump into Design in Chat for a guided character workflow or quick-generate a reference pack directly from a character description.':
    'avatarsCreateDescription',
  'Start a new avatar here. You can jump into Design in Chat for a guided character workflow, or use Quick Generate to create a reference pack directly from a character description.':
    'avatarsCreateDescription',
  'Start a new avatar here. Add a character description to create it directly, or open chat if you want to refine the character and add references first.':
    'avatarsCreateDescription',
  'Use this button to open the avatar starter. From there you can go into Design in Chat or quick-generate a reference pack from a character description.':
    'avatarsCreateMobileDescription',
  'Use this button to start a new avatar. You can go into Design in Chat or quick-generate a reference pack directly from a character description.':
    'avatarsCreateMobileDescription',
  'Use this button to start a new avatar. Add a character description to create it directly, or open chat if you want to refine the character and add references first.':
    'avatarsCreateMobileDescription',
  'Choose Your Starting Point': 'avatarChooseStartingPointTitle',
  'This page lets you pick the avatar workflow that fits the material you already have: guided builder, real model photos, or a quick prompt-led start.':
    'avatarChooseStartingPointDescription',
  'Build your avatar': 'avatarBuildTitle',
  'Start with structured identity, body, face, and hair controls when you want the most direct control before generating a preview.':
    'avatarBuildDescription',
  'Choose this path when you want to define the avatar with structured controls for identity, body, face, and hair.':
    'avatarBuildDescription',
  'Use the structured builder when you want to define the character step by step.':
    'avatarBuildMobileDescription',
  'Create an AI Twin': 'avatarAiTwinTitle',
  'Use this path when real photography should anchor the character identity before you refine details in the preview workspace.':
    'avatarAiTwinDescription',
  'Choose this path when you already have clean photos and want them to anchor a realistic AI twin.':
    'avatarAiTwinDescription',
  'Use this path when real photos should define a realistic AI twin.':
    'avatarAiTwinMobileDescription',
  'Quick Prompt And References': 'avatarQuickPromptReferencesTitle',
  'Drop in a prompt and optional image references right here to seed the same preview workspace used by the other creation flows.':
    'avatarQuickPromptReferencesDescription',
  'Write the character prompt here and attach reference images if they help define the direction. The images are optional.':
    'avatarQuickPromptReferencesDescription',
  'Add a prompt here and include reference images only if they help. The images are optional.':
    'avatarQuickPromptReferencesMobileDescription',
  'Open The Preview Workspace': 'avatarOpenPreviewWorkspaceTitle',
  'This action takes your prompt and references straight into preview generation so you can refine from there.':
    'avatarOpenPreviewWorkspaceDescription',
  'Generate Preview': 'avatarGeneratePreviewTitle',
  'Use this button to continue with the prompt and references you entered here.':
    'avatarGeneratePreviewDescription',
  'Builder Workspace': 'avatarBuilderWorkspaceTitle',
  'This top bar keeps you oriented while you move between the builder, preview review, and saved draft management.':
    'avatarBuilderWorkspaceDescription',
  'Move Through The Flow': 'avatarMoveThroughFlowTitle',
  'Before preview generation, use these sections to define the character step by step. After previews exist, this area becomes version history so you can revisit earlier directions.':
    'avatarMoveThroughFlowDescription',
  'Section Navigation': 'avatarSectionNavigationTitle',
  'Use this stepper to move between the builder sections and control which part of the avatar you are editing.':
    'avatarSectionNavigationDescription',
  'Main Builder Canvas': 'avatarMainBuilderCanvasTitle',
  'This central workspace is where you make character selections before preview generation and inspect the active preview afterward.':
    'avatarMainBuilderCanvasDescription',
  'Generate, Apply, And Confirm': 'avatarGenerateApplyConfirmTitle',
  'Use these actions to generate a preview, apply refinements to the active draft, and confirm the final avatar pack once the preview is ready.':
    'avatarGenerateApplyConfirmDescription',
  'Preview History': 'avatarPreviewHistoryTitle',
  'Older preview generations are stored in this column. Click any version to restore the draft selections that produced that generation.':
    'avatarPreviewHistoryDescription',
  'Active Preview': 'avatarActivePreviewTitle',
  'This preview shows the currently selected generation for the draft so you can inspect the result before making more changes.':
    'avatarActivePreviewDescription',
  'Review Panel': 'avatarReviewPanelTitle',
  'Use this panel to inspect and adjust the current draft details for the active preview, including character traits, references, and review settings.':
    'avatarBuilderReviewPanelDescription',
  'Use this panel to review and adjust the current draft details tied to the active preview, including identity references, extra prompt context, and character settings.':
    'avatarAiTwinReviewPanelDescription',
  'Use this panel to review and adjust the current draft details for the active preview, including supporting references and prompt refinements.':
    'avatarQuickGenerateReviewPanelDescription',
  'This panel holds the editable draft details for the active preview so you can refine the character before acting on it.':
    'avatarBuilderReviewPanelMobileDescription',
  'This panel holds the editable draft details for the active preview so you can refine the character before taking the next action.':
    'avatarAiTwinReviewPanelMobileDescription',
  'This panel holds the editable draft details for the active preview so you can refine the direction before taking the next action.':
    'avatarQuickGenerateReviewPanelMobileDescription',
  'Primary Actions': 'avatarPrimaryActionsTitle',
  'Use these controls to move the draft forward, whether that means continuing, generating a preview, applying edits, or confirming the avatar.':
    'avatarBuilderPrimaryActionsDescription',
  'Use these controls to continue the flow, generate a preview, apply edits, or confirm the avatar.':
    'avatarBuilderPrimaryActionsMobileDescription',
  'Use these controls to generate the first preview, apply your latest review edits, or confirm the avatar when it is ready.':
    'avatarAiTwinPrimaryActionsDescription',
  'Use these controls to generate a preview, apply edits, or confirm the avatar.':
    'avatarAiTwinPrimaryActionsMobileDescription',
  'Use these controls to generate a preview from the current prompt, apply your latest edits, or confirm the avatar.':
    'avatarQuickGeneratePrimaryActionsDescription',
  'Main Workspace': 'avatarMainWorkspaceTitle',
  'This is the main area for the current builder state, showing either the builder inputs or the preview you are reviewing.':
    'avatarBuilderMainWorkspaceDescription',
  'This is the main area for the current flow state, showing either the optional advanced inputs or the preview you are reviewing.':
    'avatarAiTwinMainWorkspaceDescription',
  'This is the current builder section where you set the avatar traits for the draft.':
    'avatarCurrentBuilderSectionDescription',
  'AI Twin Workspace': 'avatarAiTwinWorkspaceTitle',
  'This top bar anchors the AI Twin flow and stays with you after the route switches into preview review mode.':
    'avatarAiTwinWorkspaceDescription',
  'Identity References': 'avatarIdentityReferencesTitle',
  'Start by uploading a few clean identity angles here. Once previews exist, this space changes to saved version history for the draft.':
    'avatarIdentityReferencesDescription',
  'Refine The Direction': 'avatarRefineDirectionTitle',
  'Use the advanced prompt and reference area before generation, then inspect and refine the active preview here after it is created.':
    'avatarRefineDirectionDescription',
  'Generate the first preview here, then use the same action area to apply review edits and confirm the final avatar pack.':
    'avatarGenerateFirstPreviewDescription',
  'AI Twin Photo Uploads': 'avatarAiTwinPhotoUploadsTitle',
  'Upload the key reference angles here so the draft is anchored to the identity you want the AI Twin to preserve.':
    'avatarAiTwinPhotoUploadsDescription',
  'Advanced Inputs': 'avatarAdvancedInputsTitle',
  'Use this area for optional prompt notes and extra reference images when the photo set needs more direction.':
    'avatarAdvancedInputsDescription',
  'Quick Generate Workspace': 'avatarQuickGenerateWorkspaceTitle',
  'This top bar stays with the fast prompt-led flow before preview generation and after the route enters review mode.':
    'avatarQuickGenerateWorkspaceDescription',
  'Prompt, References, And Preview': 'avatarPromptReferencesPreviewTitle',
  'Start by composing the prompt and supporting references here. After generation, this area becomes the active preview workspace for inspection and refinement.':
    'avatarPromptReferencesPreviewDescription',
  'Prompt And References': 'avatarPromptReferencesTitle',
  'Write the character direction here and attach supporting reference images if they help sharpen the result.':
    'avatarPromptReferencesDescription',
  'Avatar Model Card': 'avatarModelCardTitle',
  'This page is the canonical reference pack for one character. It keeps the avatar identity, prompt, visibility, and generated shot set together in one place.':
    'avatarModelCardDescription',
  'This page keeps one avatar identity and its generated reference pack together.':
    'avatarModelCardMobileDescription',
  'Edit, Publish, And Regenerate': 'avatarEditPublishRegenerateTitle',
  'Use these controls to refine the avatar details, manage publishing, regenerate the pack, or delete it. This is where you keep the character usable and shareable over time.':
    'avatarEditPublishRegenerateDescription',
  'Manage The Avatar': 'avatarManageAvatarTitle',
  'Edit details, publish the avatar, or regenerate the pack from this action row.':
    'avatarManageAvatarDescription',
  'Standardized Shot Gallery': 'avatarStandardizedShotGalleryTitle',
  'These face and body shots are intentionally structured to stay consistent. They give downstream product and marketing generations a reliable visual identity to build on.':
    'avatarStandardizedShotGalleryDescription',
  'Reference Shot Gallery': 'avatarReferenceShotGalleryTitle',
  'This gallery holds the standardized shots that keep the character consistent across future generations.':
    'avatarReferenceShotGalleryDescription',
};

const MEDIA_STUDIO_WALKTHROUGH_TEXT_KEYS: Record<string, string> = {
  'Switch Work Surfaces': 'msSectionSwitcherTitle',
  'Move between Media Studio, Catalogue, Prints, and Blueprints.':
    'msSectionSwitcherSummary',
  'A product has several work surfaces. Use this menu to move between Media Studio for visuals, Catalogue Data for commerce details, and the Prints & Blueprints surfaces — all for the same product and version.':
    'msSectionSwitcherDescription',
  'Create, Chats, and Properties': 'msRailTabsTitle',
  'Three modes for this surface, all in one rail.': 'msRailTabsSummary',
  'The rail switches between Create (generate visuals), Chats (product-aware conversations tied to this surface), and Properties (commerce-ready product details). Create is open by default.':
    'msRailTabsDescription',
  'Set Brand and Avatar': 'msBrandAvatarTitle',
  'Lock in a Brand and avatars before generating.': 'msBrandAvatarSummary',
  'Pick a Brand and the avatars you want featured before you generate. IMAI uses these so lifestyle and social shots stay on-brand and on-character across the whole batch.':
    'msBrandAvatarDescription',
  'One-Click Generation': 'msSuggestionsTitle',
  'Tailored suggestions — listings, lifestyle, ads, and more.':
    'msSuggestionsSummary',
  'IMAI suggests asset batches tailored to this product. Click any suggestion to open it, tweak the prompt, and generate — or run them all to fill out the gallery fast.':
    'msSuggestionsDescription',
  'Your Asset Gallery': 'msGalleryTitle',
  'Every generated image and video lands here.': 'msGallerySummary',
  'Generated images and videos appear here, grouped by type. Hover any asset to download, edit, upscale, or mention it in chat for another pass.':
    'msGalleryDescription',
};

export function resolveWalkthroughStepText(
  text: string,
  t: (key: string) => string,
  locale?: string,
  stepId?: string
) {
  const avatarWalkthroughKey = AVATAR_WALKTHROUGH_TEXT_KEYS[text];
  if (avatarWalkthroughKey) {
    return resolveWithFallback(avatarWalkthroughKey, text, t);
  }

  // Scope the media-studio map to media-studio steps (ids prefixed `ms-`) so
  // text shared with other walkthroughs (e.g. "Your Asset Gallery", also used
  // by the legacy product tour) doesn't get globally remapped to these keys.
  if (stepId?.startsWith('ms-')) {
    const mediaStudioWalkthroughKey = MEDIA_STUDIO_WALKTHROUGH_TEXT_KEYS[text];
    if (mediaStudioWalkthroughKey) {
      return resolveWithFallback(mediaStudioWalkthroughKey, text, t);
    }
  }

  const productWalkthrough = getProductStaticCopy(locale ?? 'en').walkthrough;
  switch (text) {
    case 'Your Creative Prompt':
      return resolveWithFallback('designPromptTitle', text, t);
    case 'This is where everything starts. Describe what you want to create, then drop or paste files directly into the composer. Selected references, workspace files, and output choices appear as chips so the request carries its context with it.':
      return resolveWithFallback('designPromptDescription', text, t);
    case 'Describe what you want to create, then attach or paste files right into the composer. Selected references and output choices appear as chips.':
      return resolveWithFallback('designPromptMobileDescription', text, t);
    case 'Add Context':
      return resolveWithFallback('designAddContextTitle', text, t);
    case 'Open this menu to add product photos, design or aesthetic direction, Brand references, color palettes, trend data, workspace files, recent uploads, and connected extensions before you send.':
      return resolveWithFallback('designAddContextDescription', text, t);
    case 'Add product photos, aesthetics, Brand references, colors, trends, workspace files, recent uploads, and connected extensions before you send.':
      return resolveWithFallback('designAddContextMobileDescription', text, t);
    case 'Send Or Call':
      return resolveWithFallback('designComposerActionTitle', text, t);
    case 'This action changes with your context. Start a live IMAI call when the composer is empty, or send once you have text or references. The send menu can also request a canvas, presentation, or document.':
      return resolveWithFallback('designComposerActionDescription', text, t);
    case 'Use the right action to call IMAI or send the prompt. When you send, the output menu can ask for a canvas, presentation, or document.':
      return resolveWithFallback(
        'designComposerActionMobileDescription',
        text,
        t
      );
    case 'Project Context':
      return resolveWithFallback('designProjectContextTitle', text, t);
    case 'Choose the project this chat should belong to, or start without one. Related threads and generated work stay easier to find when they share the same project.':
      return resolveWithFallback('designProjectContextDescription', text, t);
    case 'Choose the project this chat belongs to, or start without one. Shared project context keeps related work together.':
      return resolveWithFallback(
        'designProjectContextMobileDescription',
        text,
        t
      );
    case 'Private or Shared':
      return resolveWithFallback('designVisibilityTitle', text, t);
    case 'Control who sees your work. "Only me" keeps your thread private. Switch to "Workspace" to collaborate with your team in real time.':
      return resolveWithFallback('designVisibilityDescription', text, t);
    case 'Control who sees your work. "Only me" keeps it private, "Workspace" shares with your team.':
      return resolveWithFallback('designVisibilityMobileDescription', text, t);
    case 'Deep Research':
      return resolveWithFallback('designDeepResearchTitle', text, t);
    case 'Turn this on when a request needs a slower, source-backed research pass before IMAI answers or generates. Leave it off for quick creative iteration.':
      return resolveWithFallback('designDeepResearchDescription', text, t);
    case 'Turn this on for slower, source-backed research. Leave it off when you want fast creative iteration.':
      return resolveWithFallback(
        'designDeepResearchMobileDescription',
        text,
        t
      );
    case 'Two Modes, One Workflow':
      return resolveWithFallback('designModeSwitcherTitle', text, t);
    case 'Product Design generates new concepts, patterns, and collections from your prompts. Product Studio takes your existing products and creates marketing-ready photos, social posts, and campaign assets. Switch between them anytime.':
      return resolveWithFallback('designModeSwitcherDescription', text, t);
    case 'Two Modes':
      return resolveWithFallback('designModeSwitcherMobileTitle', text, t);
    case 'Product Design generates new concepts. Product Studio creates marketing-ready assets from existing products.':
      return resolveWithFallback(
        'designModeSwitcherMobileDescription',
        text,
        t
      );
    case 'Your Workspace':
      return resolveWithFallback('designWorkspaceTitle', text, t);
    case 'Every conversation becomes a project. Your design threads, product catalog, references, Brand assets, and tech-pack projects all live here. Start a new chat anytime — your previous work is always one click away.':
      return resolveWithFallback('designWorkspaceDescription', text, t);
    case 'Tap here to access your design threads, product catalog, references, Brand assets, and settings.':
      return resolveWithFallback('designWorkspaceMobileDescription', text, t);
    case 'Workspace Navigation':
      return resolveWithFallback('designWorkspaceNavigationTitle', text, t);
    case 'Jump between the main work areas here: new chats, product studio, avatars, Brands, the library, workspace files, documents, and enabled workspace tools.':
      return resolveWithFallback(
        'designWorkspaceNavigationDescription',
        text,
        t
      );
    case 'Product Catalog':
      return resolveWithFallback('designProductCatalogTitle', text, t);
    case 'Products you create or upload live here. Each product holds all its generated variations, marketing assets, and metadata — a single source of truth you can keep building on.':
      return resolveWithFallback('designProductCatalogDescription', text, t);
    case 'Start A New Avatar':
    case 'Create An Avatar':
      return resolveWithFallback('avatarsCreateTitle', text, t);
    case 'Avatar Creation Paths':
      return resolveWithFallback('avatarsCreationPathsTitle', text, t);
    case 'Open the avatar starter here. You can jump into Design in Chat for a guided character workflow or quick-generate a reference pack directly from a character description.':
    case 'Start a new avatar here. You can jump into Design in Chat for a guided character workflow, or use Quick Generate to create a reference pack directly from a character description.':
    case 'Start a new avatar here. Add a character description to create it directly, or open chat if you want to refine the character and add references first.':
    case 'Open the avatar creation page here. From there you can choose Build your Avatar, Create an AI Twin, or Quick Generate.':
      return resolveWithFallback('avatarsCreateDescription', text, t);
    case 'Drafts':
      return resolveWithFallback('avatarsDraftsTitle', text, t);
    case 'When you have unfinished avatar work, drafts from the avatar builder, AI Twin, and quick-generate flows appear at the top of this page so you can reopen them from their saved preview history.':
      return resolveWithFallback('avatarsDraftsDescription', text, t);
    case 'Avatar Library':
    case 'Your Avatar Library':
      return resolveWithFallback('avatarsLibraryTitle', text, t);
    case 'This library brings together saved drafts, private workspace avatars, and reusable published sets from verified workspaces so you can resume, manage, and reuse characters in one place.':
    case 'Your avatar library lives here. This surface can show private workspace avatars, reusable published sets from verified workspaces, or an empty state while you are getting started.':
    case 'This library brings together drafts from the new avatar flows, private workspace avatars, and reusable published sets from verified workspaces so you can resume, manage, and reuse characters in one place.':
      return resolveWithFallback('avatarsLibraryDescription', text, t);
    case 'Use this button to open the avatar starter. From there you can go into Design in Chat or quick-generate a reference pack from a character description.':
    case 'Use this button to start a new avatar. You can go into Design in Chat or quick-generate a reference pack directly from a character description.':
    case 'Use this button to start a new avatar. Add a character description to create it directly, or open chat if you want to refine the character and add references first.':
    case 'Use this button to open the avatar creation page, where you can choose Build your Avatar, Create an AI Twin, or Quick Generate.':
      return resolveWithFallback('avatarsCreateMobileDescription', text, t);
    case 'When you have unfinished avatar work, drafts from the avatar builder, AI Twin, and quick-generate flows appear at the top of this page so you can jump back into them.':
      return resolveWithFallback('avatarsDraftsMobileDescription', text, t);
    case 'Created Avatars':
      return resolveWithFallback('avatarsCreatedTitle', text, t);
    case 'This section shows the avatars already created in your workspace so you can reopen, manage, and reuse them.':
      return resolveWithFallback('avatarsCreatedDescription', text, t);
    case 'Browse drafts, workspace avatars, and reusable published sets available to your team.':
    case 'Browse the avatars created in your workspace and any reusable published sets available to your team.':
    case 'Below your created avatars, this page can also show reusable published sets from verified workspaces, or an empty state while you are getting started.':
      return resolveWithFallback('avatarsLibraryMobileDescription', text, t);
    case 'Choose Your Starting Point':
      return text;
    case 'This page lets you pick the avatar workflow that fits the material you already have: guided builder, real model photos, or a quick prompt-led start.':
      return text;
    case 'Build your Avatar':
      return text;
    case 'Start with structured identity, body, face, and hair controls when you want the most direct control before generating a preview.':
      return text;
    case 'Create an AI Twin':
      return text;
    case 'Use this path when real photography should anchor the character identity before you refine details in the preview workspace.':
      return text;
    case 'Choose this path when you already have clean photos and want them to anchor a realistic AI twin.':
      return text;
    case 'Use this path when real photos should define a realistic AI twin.':
      return text;
    case 'Quick Prompt And References':
      return text;
    case 'Drop in a prompt and optional image references right here to seed the same preview workspace used by the other creation flows.':
      return text;
    case 'Open The Preview Workspace':
      return text;
    case 'This action takes your prompt and references straight into preview generation so you can refine from there.':
      return text;
    case 'Builder Workspace':
      return text;
    case 'This top bar keeps you oriented while you move between the builder, preview review, and saved draft management.':
      return text;
    case 'Move Through The Flow':
      return text;
    case 'Before preview generation, use these sections to define the character step by step. After previews exist, this area becomes version history so you can revisit earlier directions.':
      return text;
    case 'Main Builder Canvas':
      return text;
    case 'This central workspace is where you make character selections before preview generation and inspect the active preview afterward.':
      return text;
    case 'Generate, Apply, And Confirm':
      return text;
    case 'Use these actions to generate a preview, apply refinements to the active draft, and confirm the final avatar pack once the preview is ready.':
      return text;
    case 'AI Twin Workspace':
      return text;
    case 'This top bar anchors the AI Twin flow and stays with you after the route switches into preview review mode.':
      return text;
    case 'Identity References':
      return text;
    case 'Start by uploading a few clean identity angles here. Once previews exist, this space changes to saved version history for the draft.':
      return text;
    case 'Refine The Direction':
      return text;
    case 'Use the advanced prompt and reference area before generation, then inspect and refine the active preview here after it is created.':
      return text;
    case 'Generate the first preview here, then use the same action area to apply review edits and confirm the final avatar pack.':
      return text;
    case 'AI Twin Photo Uploads':
      return text;
    case 'Upload the key reference angles here so the draft is anchored to the identity you want the AI Twin to preserve.':
      return text;
    case 'Quick Generate Workspace':
      return text;
    case 'This top bar stays with the fast prompt-led flow before preview generation and after the route enters review mode.':
      return text;
    case 'Prompt, References, And Preview':
      return text;
    case 'Start by composing the prompt and supporting references here. After generation, this area becomes the active preview workspace for inspection and refinement.':
      return text;
    case 'Use these controls to create the first preview, apply changes during review, and confirm the final avatar pack.':
      return text;
    case 'Add Your Brand':
      return resolveWithFallback('brandingCreateTitle', text, t);
    case 'Create a Brand entry by sharing your website URL or Pinterest board. AI extracts logos, colors, and typography automatically. You can also upload images directly.':
      return resolveWithFallback('brandingCreateDescription', text, t);
    case 'Brand Library':
      return resolveWithFallback('brandingGridTitle', text, t);
    case 'Your analyzed Brands appear here. Each one captures your visual identity — logos, color palettes, typography, and social style. Use them to keep generated assets on-brand.':
      return resolveWithFallback('brandingGridDescription', text, t);
    case 'Create a Blueprint':
      return resolveWithFallback('techprintsCreateTitle', text, t);
    case "Start a tech-pack by adding a product first. You'll define measurements, materials, and specifications — IMAI generates production-ready schematics.":
      return resolveWithFallback('techprintsCreateDescription', text, t);
    case 'Your Blueprints':
      return resolveWithFallback('techprintsGridTitle', text, t);
    case 'All your tech-pack projects live here. Click any project to open the editor and work on schematics, sizing charts, and manufacturing specs.':
      return resolveWithFallback('techprintsGridDescription', text, t);
    case 'Create an Aesthetic':
      return resolveWithFallback('aestheticsCreateTitle', text, t);
    case 'Aesthetics are style guides that shape how your product images look. Create one by uploading moodboard images that capture the visual direction you want.':
      return resolveWithFallback('aestheticsCreateDescription', text, t);
    case 'Your Style Library':
      return resolveWithFallback('aestheticsGridTitle', text, t);
    case "Browse existing aesthetics here. You don't need to pick one manually — when you generate product images, IMAI automatically selects the best aesthetic and builds the right world around your product.":
      return resolveWithFallback('aestheticsGridDescription', text, t);
    case 'Generate Assets':
      return productWalkthrough.productSuggestionsTitle;
    case 'One-click generation for different use cases — product listings, lifestyle shots, social ads, UGC content, and more. Pick a type and IMAI generates a batch instantly.':
      return productWalkthrough.productSuggestionsDescription;
    case 'Create Or Use Avatars':
      return productWalkthrough.productAvatarsTitle;
    case 'This section lets you build a consistent character workflow for the product. If you do not have avatars yet, jump to Design to create them. If you do, select up to 5 here and use them for lifestyle and social image generations.':
      return productWalkthrough.productAvatarsDescription;
    case 'Choose Your Work Surface':
      return productWalkthrough.productTabsTitle;
    case 'These tabs do more than organize outputs. They switch the product into different working modes. Media Studio is for visual asset creation, Catalogue Data is for commerce-ready product information, Prints & Patterns is for repeatable design exploration, and tech-pack is for production and manufacturing workflows.':
      return productWalkthrough.productTabsDescription;
    case 'Your Asset Gallery':
      return productWalkthrough.productGalleryTitle;
    case 'Every generated image, video, and 3D model lives here. Hover over any asset to download, upscale, edit with inpainting, or mention it in chat for further refinement.':
      return productWalkthrough.productGalleryDescription;
    case 'Add or Generate Assets':
      return productWalkthrough.productAddAssetTitle;
    case 'Upload your own photos and videos, or generate new ones for this group. Each asset group organizes your content by campaign, style, or use case.':
      return productWalkthrough.productAddAssetDescription;
    case 'Chat Workspace':
      return productWalkthrough.productChatTabsTitle;
    case 'This area is a product-specific workspace, not a generic chat. The active thread keeps you inside one line of thinking, while the Chats view lets you move between saved conversations for this product and version.':
      return productWalkthrough.productChatTabsDescription;
    case 'Branch Into New Directions':
      return productWalkthrough.productChatNewTitle;
    case 'Use New chat to start a fresh exploration without overwriting the current conversation. This is the fastest way to compare different creative or strategic directions side by side.':
      return productWalkthrough.productChatNewDescription;
    case 'Use Mentions And Context':
      return productWalkthrough.productChatInputTitle;
    case 'This composer understands the current product, version, and work surface. You can mention assets, groups, and other references directly in chat so IMAI works from precise context instead of text alone.':
      return productWalkthrough.productChatInputDescription;
    case 'Version History':
      return productWalkthrough.productVersionsTitle;
    case 'Each version captures a snapshot of your product. Create new versions to explore different directions without losing your previous work.':
      return productWalkthrough.productVersionsDescription;
    case 'Swipe through these tabs to switch the product into different working modes. Media Studio is for visuals, Catalogue Data is for listings, Prints & Patterns is for repeatable design exploration, and tech-pack is for production workflows.':
      return productWalkthrough.mobileProductTabsDescription;
    case 'Product Workspace Chat':
      return productWalkthrough.mobileProductChatTitle;
    case 'Tap to open a chat that stays tied to this product and the surface you are viewing. Ask for variations, style changes, or next steps, and IMAI responds with product-aware context.':
      return productWalkthrough.mobileProductChatDescription;

    // Step summaries (#2). Short benefit-first lines shown by default, with the
    // longer description tucked behind "Learn more". Routed through
    // resolveWithFallback (WalkthroughSteps namespace) so any locale missing a
    // key falls back to the English source string.
    case 'Type what you want — drop in files for context.':
      return resolveWithFallback('designPromptSummary', text, t);
    case 'Add photos, Brands, colors, and more before sending.':
      return resolveWithFallback('designAddContextSummary', text, t);
    case 'Send your prompt, or start a live IMAI call.':
      return resolveWithFallback('designComposerActionSummary', text, t);
    case 'Group this chat under a project to find it later.':
      return resolveWithFallback('designProjectContextSummary', text, t);
    case 'Keep it private, or share with your workspace.':
      return resolveWithFallback('designVisibilitySummary', text, t);
    case 'Slower, source-backed answers when you need them.':
      return resolveWithFallback('designDeepResearchSummary', text, t);
    case 'Switch between designing products and marketing them.':
      return resolveWithFallback('designModeSwitcherSummary', text, t);
    case 'All your chats, products, and assets live here.':
      return resolveWithFallback('designWorkspaceSummary', text, t);
    case 'Jump between chats, studio, avatars, and more.':
      return resolveWithFallback('designWorkspaceNavigationSummary', text, t);
    case 'Every product you make or upload lives here.':
      return resolveWithFallback('designProductCatalogSummary', text, t);
    case 'One-click batches: listings, lifestyle, ads, UGC.':
      return resolveWithFallback('productSuggestionsSummary', text, t);
    case 'Add up to 5 avatars for lifestyle and social shots.':
      return resolveWithFallback('productAvatarsSummary', text, t);
    case 'Each tab is a different work mode for this product.':
      return resolveWithFallback('productTabsSummary', text, t);
    case 'Every image, video, and 3D model lands here.':
      return resolveWithFallback('productGallerySummary', text, t);
    case 'Upload or generate assets for this group.':
      return resolveWithFallback('productAddAssetSummary', text, t);
    case 'A product-aware chat, not a generic one.':
      return resolveWithFallback('productChatTabsSummary', text, t);
    case 'Start a fresh chat without losing this one.':
      return resolveWithFallback('productChatNewSummary', text, t);
    case 'Mention assets so IMAI works from exact context.':
      return resolveWithFallback('productChatInputSummary', text, t);
    case 'Snapshot the product to try directions safely.':
      return resolveWithFallback('productVersionsSummary', text, t);
    case 'Open a product-aware chat for this surface.':
      return resolveWithFallback('mobileProductChatSummary', text, t);
    case 'Start an avatar: builder, AI Twin, or quick generate.':
      return resolveWithFallback('avatarsCreationPathsSummary', text, t);
    case 'Pick up unfinished avatar work here.':
      return resolveWithFallback('avatarsDraftsSummary', text, t);
    case 'All your avatars and reusable sets in one place.':
      return resolveWithFallback('avatarsLibrarySummary', text, t);
    case 'Avatars you have already made.':
      return resolveWithFallback('avatarsCreatedSummary', text, t);
    case 'Define identity, body, face, and hair by hand.':
      return resolveWithFallback('avatarBuildSummary', text, t);
    case 'Anchor a realistic twin to your photos.':
      return resolveWithFallback('avatarAiTwinSummary', text, t);
    case 'Describe the character; add references if useful.':
      return resolveWithFallback('avatarQuickPromptSummary', text, t);
    case 'Continue with your prompt and references.':
      return resolveWithFallback('avatarGeneratePreviewSummary', text, t);
    case 'Move between builder sections here.':
      return resolveWithFallback('avatarSectionNavigationSummary', text, t);
    case 'Reopen any past generation to restore it.':
      return resolveWithFallback('avatarPreviewHistorySummary', text, t);
    case 'Set the avatar traits for this draft.':
      return resolveWithFallback('avatarBuilderWorkspaceSummary', text, t);
    case 'The generation currently selected for this draft.':
      return resolveWithFallback('avatarActivePreviewSummary', text, t);
    case 'Inspect and tweak the active draft details.':
      return resolveWithFallback('avatarReviewPanelSummary', text, t);
    case 'Continue, generate, apply edits, or confirm.':
      return resolveWithFallback('avatarPrimaryActionsSummary', text, t);
    case 'Your builder inputs or the preview, depending on state.':
      return resolveWithFallback('avatarMainWorkspaceSummary', text, t);
    case 'Upload key angles to anchor the identity.':
      return resolveWithFallback('avatarPhotoUploadsSummary', text, t);
    case 'Optional prompt notes and extra references.':
      return resolveWithFallback('avatarAdvancedInputsSummary', text, t);
    case 'Write the direction; add references to sharpen it.':
      return resolveWithFallback('avatarPromptReferencesSummary', text, t);
    case 'One character: identity, prompt, and shot set.':
      return resolveWithFallback('avatarModelCardSummary', text, t);
    case 'Refine, publish, regenerate, or delete.':
      return resolveWithFallback('avatarEditPublishSummary', text, t);
    case 'Consistent face and body shots to build on.':
      return resolveWithFallback('avatarShotGallerySummary', text, t);
    case 'Edit, publish, or regenerate the pack.':
      return resolveWithFallback('avatarManageSummary', text, t);
    case 'Standardized shots that keep the character consistent.':
      return resolveWithFallback('avatarReferenceGallerySummary', text, t);
    case 'A style guide built from moodboard images.':
      return resolveWithFallback('aestheticsCreateSummary', text, t);
    case 'Browse styles; IMAI auto-picks the best fit.':
      return resolveWithFallback('aestheticsGridSummary', text, t);
    case 'Pull logos, colors, and fonts from a URL.':
      return resolveWithFallback('brandingCreateSummary', text, t);
    case 'Your Brand identities for on-brand assets.':
      return resolveWithFallback('brandingGridSummary', text, t);
    case 'Turn a product into production-ready specs.':
      return resolveWithFallback('techprintsCreateSummary', text, t);
    case 'Open any blueprint to edit schematics and specs.':
      return resolveWithFallback('techprintsGridSummary', text, t);

    default:
      return text;
  }
}
